<script lang="ts" setup>
import { AppButton, AppPassword, AppTextField, AppTypography } from '#core/components';
import type { CommonErrorResponse } from '#core/types/ErrorResponse';
import { storeToRefs } from 'pinia';
import { computed } from 'vue';
import { useAuthStore } from '../store/auth';
import type { LoginForm } from '../types';

const { VALIDATIONS, PASSWORD_VALIDATION } = useValidation();
const { form, handleSubmit, getErrors } = useForm<LoginForm>({
  form: {
    email: '',
    password: '',
  },
  rules: {
    email: {
      required: VALIDATIONS.required('Email'),
      email: VALIDATIONS.email(),
    },
    password: PASSWORD_VALIDATION,
  },
});

const { user, isVerified, hasCompletedOnboarding } = storeToRefs(useAuthStore());
const router = useRouter();

const {
  pending: verifyLoading,
  execute: execSendVerify,
  error: verifyError,
} = useSubmit<{}, { data: CommonErrorResponse }>();

const { setToken } = useAuthState();
const { getSession } = useAuth();
const loginError = ref({ data: { message: '' } });

const { data, error, pending: loginLoading, execute: execSubmit } = useSubmit();
async function handleSignIn(): Promise<void> {
  handleSubmit(async function () {
    try {
      loginLoading.value = true;
      await execSubmit('auth/login', {
        method: 'POST',
        body: {
          username: form.value.email,
          password: form.value.password,
        },
      });

      if (error.value) {
        throw error.value;
      }

      if (data.value) {
        setToken(data.value.data.access_token);
        await getSession();
        user.value = data.value.data.user;
        await nextTick();

        if (!isVerified.value) {
          await execSendVerify('auth/verification/resend', {
            method: 'POST',
            body: { via: 'email' },
          });

          if (!verifyError.value) {
            router.push({ name: 'verify' });
          }
          return;
        }

        if (!hasCompletedOnboarding.value) {
          router.push({ name: 'onboarding' });
          return;
        }

        router.push({ name: 'index' });
      }
    } catch (error) {
      loginError.value.data = error.data;
    } finally {
      loginLoading.value = false;
    }
  });
}

const errors = computed(() => {
  return getErrors({ email: loginError.value?.data?.message });
});
</script>

<template>
  <v-form @submit.prevent="handleSignIn" class="d-flex flex-column gap-2xl">
    <div class="d-flex flex-column gap-xs align-center">
      <AppTypography element="h4" class="font-h4">Welcome Back!</AppTypography>
      <AppTypography element="p" class="text-center">Enter your email and password to log in</AppTypography>
    </div>

    <div class="d-flex flex-column gap-xl">
      <AppTextField
        v-model="form.email"
        :error-messages="errors.email"
        label="Email"
        :readonly="loginLoading"
        autocomplete="email"
        hideDetails="auto"
        data-testid="email"
        autofocus
        data-cy="email"
      >
      </AppTextField>

      <AppPassword v-model="form.password" :error-messages="errors.password" class="mb-1" data-cy="password" />

      <AppButton block variant="tertiary" to="/reset-password">
        <AppTypography class="font-body-bold">Forgot Password</AppTypography>
      </AppButton>

      <AppButton block data-cy="submit" data-testid="submit" type="submit" :loading="loginLoading || verifyLoading">
        <AppTypography class="font-body-bold">Continue</AppTypography>
      </AppButton>

      <div class="divider-container d-flex align-center gap-xl">
        <div class="divider-line" />
        <AppTypography class="divider-text">Or</AppTypography>
        <div class="divider-line" />
      </div>
    </div>

    <AppTypography element="span" type="headline-2" class="d-block text-center">
      Don't have an account yet?
      <nuxt-link to="/register" class="text-decoration-none font-body-bold ml-2" data-cy="sign-up">Sign Up</nuxt-link>
    </AppTypography>
  </v-form>
</template>

<style lang="scss" scoped>
.divider-container {
  width: 100%;
}

.divider-line {
  flex: 1;
  height: 1px;
  background-color: rgb(var(--v-theme-border-default));
}

.divider-text {
  flex-shrink: 0;
  white-space: nowrap;
  color: rgb(var(--v-theme--text-tertiary));
}
</style>
